// Wearable battery log (firmware v0.17.0): the battery % sampled every 60 s
// into the wearable's own flash ring, read over USB (`battery` in `ui on`
// mode) or through a case (`dashboard battery-log [<MAC>]`, case software
// v0.11.0). Pure helpers: parse the serial dump, check the case allowlist
// argument, and shape readings into what BatteryHistoryChart draws.
//
// A reading: { epoch, boot, up, mv, pct, state }
//   epoch  UTC seconds; 0 = the wall clock was never set during that boot
//   boot   boot number; a change of boot = the device was off in between
//   up     seconds since that boot
//   state  "on-batt" | "charging" | "charged"

import { parseSerialLine } from "./serialEvents";

/** First firmware with the battery log over USB and Bluetooth. */
export const BATTERY_LOG_FW_MIN = [0, 17, 0];
export const BATTERY_LOG_FW_LABEL = "v0.17.0";
/** First case software with `dashboard battery-log`. */
export const BATTERY_LOG_CASE_MIN = "v0.11.0";

export const BATTERY_STATES = ["on-batt", "charging", "charged"];

/** A MAC the case accepts in `battery-log <MAC>` (upper-case, colons). */
export const BATTERY_LOG_MAC_RE = /^[0-9A-F]{2}(:[0-9A-F]{2}){5}$/;

/** `battery-log <MAC>` for a MAC in any case/separator, or null if invalid. */
export function batteryLogCommand(mac) {
  const hex = String(mac || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();
  if (hex.length !== 12) return null;
  const colon = hex.match(/../g).join(":");
  return BATTERY_LOG_MAC_RE.test(colon) ? `battery-log ${colon}` : null;
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** One reading with its fields checked, or null if it isn't usable. */
export function normalizeReading(raw) {
  if (!raw || typeof raw !== "object") return null;
  const pct = num(raw.pct);
  const mv = num(raw.mv);
  const boot = num(raw.boot);
  const up = num(raw.up);
  if (pct == null || boot == null || up == null) return null;
  const epoch = num(raw.epoch);
  return {
    epoch: epoch && epoch > 0 ? epoch : 0,
    boot,
    up: Math.max(0, up),
    mv: mv ?? null,
    pct: Math.max(0, Math.min(100, pct)),
    state: BATTERY_STATES.includes(raw.state) ? raw.state : "on-batt",
  };
}

/**
 * Parse the lines of a `battery` reply. Prose lines (anything not starting
 * with `{`) and unrelated events are ignored. Returns
 *   { found, header, readings, complete }
 * found     a `batt_log` header arrived (false = firmware older than v0.17.0)
 * header    { n, interval_s, boot } from `batt_log`
 * complete  `batt_end` arrived (or `ended`: the caller saw it and stripped
 *           it) and the count matches the header
 */
export function parseBatteryLogLines(lines, { ended = false } = {}) {
  let header = null;
  let end = null;
  const readings = [];
  for (const line of lines || []) {
    const o = parseSerialLine(line);
    if (!o) continue;
    if (o.t === "batt_log") {
      // A second header means a second dump: keep only the newest.
      header = {
        n: num(o.n) ?? 0,
        interval_s: num(o.interval_s) || 60,
        boot: num(o.boot),
      };
      readings.length = 0;
      end = null;
    } else if (o.t === "batt" && header) {
      const r = normalizeReading(o);
      if (r) readings.push(r);
    } else if (o.t === "batt_end" && header) {
      end = { n: num(o.n) };
    }
  }
  const expected = end?.n ?? header?.n ?? null;
  return {
    found: !!header,
    header,
    readings,
    complete:
      (!!end || (ended && !!header)) &&
      (expected == null || readings.length === expected),
  };
}

// ── Chart shaping ──────────────────────────────────────────────────────────

const MIN = 60;
const HOUR = 3600;

/** "+2h 15m", "+45m", "+30s" — time since a boot started. */
export function fmtUptime(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / HOUR);
  const m = Math.floor((s % HOUR) / MIN);
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `+${d}d ${h % 24}h`;
  }
  if (h > 0) return `+${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `+${m}m`;
  return `+${s}s`;
}

/** Group readings (oldest first) into boots, in the order they appear. */
export function splitBoots(readings) {
  const boots = [];
  let cur = null;
  for (const r of readings) {
    if (!cur || r.boot !== cur.boot) {
      cur = { boot: r.boot, readings: [] };
      boots.push(cur);
    }
    cur.readings.push(r);
  }
  for (const b of boots) {
    b.firstUp = b.readings[0].up;
    b.lastUp = b.readings[b.readings.length - 1].up;
    const known = b.readings.find((r) => r.epoch > 0);
    // The device back-fills epoch for a whole boot once its clock is set,
    // so one known reading pins the boot: start = epoch - up.
    b.startEpoch = known ? known.epoch - known.up : null;
    b.clockKnown = b.startEpoch != null;
  }
  return boots;
}

/**
 * Lay out readings on an x axis (seconds) and work out the background bands.
 *
 * mode "time"      x is UTC seconds. Boots without a clock sit in order
 *                  between their neighbours (centred in the gap they must
 *                  have happened in), or just before/after the known ones;
 *                  they are flagged `estimated` and never shown with a clock
 *                  time.
 * mode "sequence"  no boot has a clock (or the estimate can't be placed
 *                  honestly): boots are laid end to end with a fixed gap,
 *                  and x is on-time since the first reading.
 *
 * Returns { mode, points, bands, offs, boots, domain, intervalS, latest }
 *   points  [{ x, pct, mv, state, boot, up, epoch, estimated }] with a
 *           { x, pct: null } break between boots and across long holes
 *   bands   [{ x1, x2, state, estimated }] — contiguous runs of one state
 *   offs    [{ x1, x2, fromBoot, toBoot }] — the device was off
 *   boots   [{ boot, x1, x2, clockKnown, estimated }]
 */
export function shapeBatteryLog(input, { intervalS = 60 } = {}) {
  const readings = (input || []).map(normalizeReading).filter(Boolean);
  const interval = intervalS > 0 ? intervalS : 60;
  const empty = {
    mode: "time",
    points: [],
    bands: [],
    offs: [],
    boots: [],
    domain: [0, 1],
    intervalS: interval,
    latest: null,
  };
  if (!readings.length) return empty;

  const boots = splitBoots(readings);
  const totalOn = boots.reduce(
    (a, b) => a + (b.lastUp - b.firstUp) + interval,
    0
  );
  // The visual spacer for an "off" stretch whose length we don't know.
  const spacer = Math.max(10 * MIN, Math.round(totalOn * 0.04));

  const layout = layoutBoots(boots, interval, spacer);
  const mode = layout ? "time" : "sequence";
  const placed = layout || layoutSequence(boots, interval, spacer);

  const points = [];
  const bands = [];
  const offs = [];
  const bootSpans = [];
  // A hole longer than this inside one boot is drawn as a break.
  const holeS = interval * 5;

  boots.forEach((b, bi) => {
    const { origin, estimated } = placed[bi];
    const xOf = (r) => origin + r.up;
    const x1 = xOf(b.readings[0]);
    const x2 = xOf(b.readings[b.readings.length - 1]) + interval;
    bootSpans.push({
      boot: b.boot,
      x1,
      x2,
      clockKnown: b.clockKnown,
      estimated,
    });
    if (bi > 0) {
      const prev = bootSpans[bi - 1];
      points.push({ x: (prev.x2 + x1) / 2, pct: null });
      offs.push({
        x1: prev.x2,
        x2: x1,
        fromBoot: prev.boot,
        toBoot: b.boot,
      });
    }
    let band = null;
    b.readings.forEach((r, i) => {
      const x = xOf(r);
      if (i > 0 && r.up - b.readings[i - 1].up > holeS) {
        points.push({ x: x - interval / 2, pct: null });
        if (band) {
          bands.push(band);
          band = null;
        }
      }
      points.push({
        x,
        pct: r.pct,
        mv: r.mv,
        state: r.state,
        boot: r.boot,
        up: r.up,
        epoch: b.clockKnown ? b.startEpoch + r.up : 0,
        estimated,
      });
      const next = b.readings[i + 1];
      const end =
        next && next.up - r.up <= holeS ? origin + next.up : x + interval;
      if (band && band.state === r.state && band.x2 >= x) {
        band.x2 = end;
      } else {
        if (band) bands.push(band);
        band = { x1: x, x2: end, state: r.state, estimated };
      }
    });
    if (band) bands.push(band);
  });

  const last = readings[readings.length - 1];
  return {
    mode,
    points,
    bands,
    offs,
    boots: bootSpans,
    domain: [bootSpans[0].x1, bootSpans[bootSpans.length - 1].x2],
    intervalS: interval,
    latest: last,
  };
}

// Real-time layout, or null when it can't be done honestly (no boot has a
// clock, known boots out of order, or a clock-less boot doesn't fit between
// its neighbours).
function layoutBoots(boots, interval, spacer) {
  if (!boots.some((b) => b.clockKnown)) return null;
  const placed = boots.map((b) =>
    b.clockKnown ? { origin: b.startEpoch, estimated: false } : null
  );
  const spanEnd = (b, p) => p.origin + b.lastUp + interval;
  const spanStart = (b, p) => p.origin + b.firstUp;
  // Known boots must not overlap or run backwards (a clock set wrong).
  let prevEnd = -Infinity;
  for (let i = 0; i < boots.length; i++) {
    if (!placed[i]) continue;
    if (spanStart(boots[i], placed[i]) < prevEnd - interval) return null;
    prevEnd = spanEnd(boots[i], placed[i]);
  }
  for (let i = 0; i < boots.length; i++) {
    if (placed[i]) continue;
    // A run of clock-less boots [i, j).
    let j = i;
    while (j < boots.length && !placed[j]) j++;
    const run = boots.slice(i, j);
    const runLen =
      run.reduce((a, b) => a + (b.lastUp - b.firstUp) + interval, 0) +
      spacer * (run.length - 1);
    const before = i > 0 ? spanEnd(boots[i - 1], placed[i - 1]) : null;
    const after = j < boots.length ? spanStart(boots[j], placed[j]) : null;
    let start;
    if (before != null && after != null) {
      const room = after - before;
      if (runLen > room) return null;
      start = before + (room - runLen) / 2;
    } else if (before != null) {
      start = before + spacer;
    } else {
      start = after - spacer - runLen;
    }
    let x = start;
    for (let k = i; k < j; k++) {
      const b = boots[k];
      placed[k] = { origin: x - b.firstUp, estimated: true };
      x += b.lastUp - b.firstUp + interval + spacer;
    }
    i = j - 1;
  }
  return placed;
}

// End-to-end layout: x = on-time since the first reading, plus a spacer
// between boots.
function layoutSequence(boots, interval, spacer) {
  let x = 0;
  return boots.map((b) => {
    const p = { origin: x - b.firstUp, estimated: !b.clockKnown };
    x += b.lastUp - b.firstUp + interval + spacer;
    return p;
  });
}

/** Readings from a case `battery-log <MAC>` result, or [] for anything else. */
export function readingsFromCase(json) {
  if (!json || json.ok === false || !Array.isArray(json.samples)) return [];
  return json.samples.map(normalizeReading).filter(Boolean);
}

const TICK_STEPS = [
  15 * MIN,
  30 * MIN,
  HOUR,
  2 * HOUR,
  3 * HOUR,
  6 * HOUR,
  12 * HOUR,
  24 * HOUR,
  48 * HOUR,
];

/**
 * Axis ticks (x values) for a shaped log: about `target` of them, on round
 * local hours for "time" and on round hours of on-time for "sequence".
 */
export function batteryTicks(shaped, target = 6) {
  if (!shaped || !shaped.points.length) return [];
  const [lo, hi] = shaped.domain;
  const span = Math.max(1, hi - lo);
  const step =
    TICK_STEPS.find((s) => span / s <= target) ||
    Math.ceil(span / target / (24 * HOUR)) * 24 * HOUR;
  const ticks = [];
  if (shaped.mode === "time") {
    // Round to the step in local time (so 6 h ticks land on 00/06/12/18).
    const tz = new Date(lo * 1000).getTimezoneOffset() * 60;
    let x = Math.ceil((lo - tz) / step) * step + tz;
    for (; x <= hi; x += step) ticks.push(x);
  } else {
    for (let x = lo; x <= hi; x += step) ticks.push(x);
  }
  return ticks;
}
